<template>
  <div>
    <!-- Back-style header (derived from the checkout header in the design) -->
    <SiteHeaderBack size="lg" />

    <section class="mx-auto flex max-w-115 flex-col px-6 py-16 sm:py-24">
      <h1 class="text-center text-3xl text-(--brand-taupe) font-normal tracking-[0.18em] uppercase">
        {{ t('Přihlášení') }}
      </h1>

      <!-- Krok 1: přihlašovací údaje -->
      <form v-if="!challenge" class="mt-10 flex flex-col gap-5" @submit.prevent="onSubmit">
        <label class="flex flex-col gap-2">
          <span class="text-sm tracking-[0.16em] uppercase text-(--brand-muted)">
            {{ t('E-mail') }}
          </span>
          <input
            v-model="email"
            type="email"
            autocomplete="email"
            required
            class="border border-(--brand-taupe) px-4 py-3 outline-none focus:border-(--brand-black)"
          />
        </label>

        <label class="flex flex-col gap-2">
          <span class="text-sm tracking-[0.16em] uppercase text-(--brand-muted)">
            {{ t('Heslo') }}
          </span>
          <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            required
            class="border border-(--brand-taupe) px-4 py-3 outline-none focus:border-(--brand-black)"
          />
        </label>

        <NuxtLinkLocale
          :to="{ name: 'reset-password', query: { email: email || undefined } }"
          class="-mt-2 self-end text-sm text-(--brand-muted) underline hover:text-(--brand-ink)"
          >{{ t('Zapomenuté heslo?') }}</NuxtLinkLocale
        >

        <p v-if="errorMessage" class="text-base text-red-700">{{ errorMessage }}</p>

        <SiteButton
          type="submit"
          variant="solid"
          tone="dark"
          :loading="loading"
          class="mt-2 w-full"
        >
          {{ t('Přihlásit se') }}
        </SiteButton>
      </form>

      <!-- Krok 2: dvoufázové ověření (OTP) -->
      <form v-else class="mt-10 flex flex-col gap-5" @submit.prevent="onSubmitOtp">
        <p class="text-center text-base text-(--brand-muted)">{{ otpHint }}</p>

        <label class="flex flex-col gap-2">
          <span class="text-sm tracking-[0.16em] uppercase text-(--brand-muted)">{{
            t('Ověřovací kód')
          }}</span>
          <input
            ref="otpInput"
            v-model="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            pattern="[0-9]*"
            required
            class="border border-(--brand-taupe) px-4 py-3 text-center text-2xl tracking-[0.5em] outline-none focus:border-(--brand-black)"
          />
        </label>

        <p v-if="otpError" class="text-base text-red-700">{{ otpError }}</p>

        <SiteButton
          type="submit"
          variant="solid"
          tone="dark"
          :loading="otpLoading"
          class="mt-2 w-full"
        >
          {{ t('Ověřit a přihlásit se') }}
        </SiteButton>

        <button
          type="button"
          class="self-center text-sm text-(--brand-muted) underline hover:text-(--brand-ink)"
          @click="resetChallenge"
        >
          {{ t('Zpět na přihlášení') }}
        </button>
      </form>

      <p class="mt-8 text-center text-base text-(--brand-muted)">
        {{ t('Ještě nejsi členem?') }}
        <NuxtLinkLocale :to="{ name: 'membership-order' }" class="text-(--brand-ink) underline">{{
          t('Vstoupit do klubu')
        }}</NuxtLinkLocale>
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { LoginChallenge } from '@@/shared-components/types/Auth'

definePageMeta({ public: true, loginPage: true })

const {
  $i18n: { t },
  $auth,
} = useNuxtApp()

usePageSeo({ title: () => t('Přihlášení') })

// `?email=` prefill — used when checkout hits code 751 (e-mail already has an account).
const route = useRoute()
const email = ref(typeof route.query.email === 'string' ? route.query.email : '')
const password = ref('')
const loading = ref(false)
const errorMessage = ref('')

// Dvoufázové ověření: 202 z přihlášení vrátí challenge, který dokončíme OTP kódem.
const challenge = ref<LoginChallenge | null>(null)
const code = ref('')
const otpLoading = ref(false)
const otpError = ref('')
const otpInput = ref<HTMLInputElement | null>(null)

// Nápověda podle doručovací metody OTP; backend překládá tyto klíče.
const otpHintMap: Record<string, string> = {
  email: t('Zadej ověřovací kód, který jsme ti poslali na e-mail.'),
  sms: t('Zadej ověřovací kód, který jsme ti poslali v SMS.'),
  totp: t('Zadej ověřovací kód z autentizační aplikace.'),
  push: t('Potvrď přihlášení v aplikaci a zadej zobrazený kód.'),
}
const otpHint = computed(
  () =>
    (challenge.value?.challengeMethod && otpHintMap[challenge.value.challengeMethod]) ||
    t('Zadej ověřovací kód pro dokončení přihlášení.')
)

const onSubmit = async () => {
  if (loading.value) return
  loading.value = true
  errorMessage.value = ''
  try {
    // afterLoginLogic inside $auth.login handles the post-login redirect.
    // Na 202 (vyžadováno OTP) vrátí login challenge místo přesměrování.
    const { challenge: ch } = await $auth.login({ email: email.value, password: password.value })
    if (ch) {
      challenge.value = ch
      await nextTick()
      otpInput.value?.focus()
    }
  } catch (e) {
    const err = e as { response?: { _data?: { errors?: { message?: string }[] } } }
    errorMessage.value =
      err?.response?._data?.errors?.[0]?.message ?? t('Přihlášení se nezdařilo. Zkontroluj údaje.')
  } finally {
    loading.value = false
  }
}

const onSubmitOtp = async () => {
  if (otpLoading.value || !challenge.value) return
  otpLoading.value = true
  otpError.value = ''
  try {
    // completeChallenge nastaví session a přesměruje (afterLoginLogic).
    await $auth.completeChallenge(challenge.value.challengeId, code.value)
  } catch (e) {
    const err = e as { response?: { _data?: { errors?: { message?: string }[] } } }
    otpError.value =
      err?.response?._data?.errors?.[0]?.message ?? t('Ověření se nezdařilo. Zkontroluj kód.')
    code.value = ''
    otpInput.value?.focus()
  } finally {
    otpLoading.value = false
  }
}

const resetChallenge = () => {
  challenge.value = null
  code.value = ''
  otpError.value = ''
  password.value = ''
}
</script>
